import type { ReactNode } from "react";
import { AlertTriangle, Clock, Eye, Info, RotateCw, ServerCrash } from "lucide-react";

/**
 * Стани помічника (мокап v2 §0/§20). LOCKED-copy. A11y: кожен стан = іконка +
 * текст (не color-only); CTA — реальні кнопки з видимою причиною.
 */

type Variant = "neutral" | "warn" | "info" | "err";

const VARIANT_CLS: Record<Variant, string> = {
  neutral: "border-dashed border-border bg-background",
  warn: "border-warning-foreground/25 bg-warning",
  info: "border-info-foreground/25 bg-info",
  err: "border-destructive-soft-foreground/25 bg-destructive-soft",
};
const TITLE_CLS: Record<Variant, string> = {
  neutral: "text-foreground",
  warn: "text-warning-foreground",
  info: "text-info-foreground",
  err: "text-destructive-soft-foreground",
};

function StateCard({
  variant,
  icon,
  title,
  desc,
  cta,
}: {
  variant: Variant;
  icon: ReactNode;
  title: string;
  desc: ReactNode;
  cta?: { label: string; onClick: () => void; tone?: "primary" | "plain" };
}) {
  return (
    <div className={`rounded-lg border p-4 ${VARIANT_CLS[variant]}`}>
      <div className="flex items-start gap-2.5">
        <span className={`mt-0.5 shrink-0 ${TITLE_CLS[variant]}`} aria-hidden>
          {icon}
        </span>
        <div className="min-w-0">
          <div className={`text-[13px] font-semibold ${TITLE_CLS[variant]}`}>{title}</div>
          <div className="mt-1 text-xs text-muted-foreground">{desc}</div>
          {cta && (
            <button
              type="button"
              onClick={cta.onClick}
              className={
                cta.tone === "plain"
                  ? "mt-2.5 inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent"
                  : "mt-2.5 inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary-soft px-3 py-1.5 text-xs font-semibold text-primary-dark hover:bg-primary-soft/70"
              }
            >
              {cta.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function NoCoverageState({ onAskAnother }: { onAskAnother: () => void }) {
  return (
    <StateCard
      variant="neutral"
      icon={<Info className="h-4 w-4" />}
      title="База знань поки не покриває це питання"
      desc="Сформулюйте інакше або запитайте керівника чи адміністратора."
      cta={{ label: "Поставити інше питання", onClick: onAskAnother }}
    />
  );
}

export function PiiWarningState({
  uiHint,
  onRephrase,
}: {
  uiHint?: string | null;
  onRephrase: () => void;
}) {
  return (
    <StateCard
      variant="warn"
      icon={<AlertTriangle className="h-4 w-4" />}
      title="Схоже, у питанні є дані клієнта"
      desc={
        <>
          {uiHint || "Не вводьте назви клієнтів, ЄДРПОУ, телефони чи номери документів."}{" "}
          Чутливі частини буде замасковано або питання буде відхилене.
        </>
      }
      cta={{ label: "Переформулювати питання", onClick: onRephrase }}
    />
  );
}

export function RateLimitState() {
  return (
    <StateCard
      variant="info"
      icon={<Clock className="h-4 w-4" />}
      title="Забагато запитів"
      desc="Зачекайте хвилинку і спробуйте ще раз (ліміт захищає сервіс для всіх)."
    />
  );
}

export function UnavailableState({ onRetry }: { onRetry: () => void }) {
  return (
    <StateCard
      variant="err"
      icon={<ServerCrash className="h-4 w-4" />}
      title="Помічник тимчасово недоступний"
      desc="Ваше питання не втрачено — спробуйте за хвилину."
      cta={{ label: "Повторити", onClick: onRetry, tone: "plain" }}
    />
  );
}

/** AC-AI-DIRECTOR-COPY (LOCKED) — режим перегляду, 0 обіцянок таймінгу. */
export function DirectorNote() {
  return (
    <StateCard
      variant="info"
      icon={<Eye className="h-4 w-4" />}
      title="Директор — режим перегляду"
      desc="Відповіді пояснюють, як працює процес; кроки дій виконує менеджер/керівник. Дії директора можуть бути увімкнені лише окремим рішенням (DirectorAction)."
    />
  );
}

export function ThinkingSkeleton() {
  return (
    <div className="rounded-lg border border-border p-4" aria-label="Помічник думає" aria-busy>
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <RotateCw className="h-3 w-3 animate-spin" aria-hidden /> Думає…
      </div>
      <div className="space-y-2">
        <div className="h-2.5 w-[90%] animate-pulse rounded bg-muted" />
        <div className="h-2.5 w-[75%] animate-pulse rounded bg-muted" />
        <div className="h-2.5 w-[82%] animate-pulse rounded bg-muted" />
      </div>
    </div>
  );
}
